"use client";

import { SeriesModel, SeriesAccessModel } from "@api/models";
import { useEffect, useState, useSyncExternalStore } from "react";
import EpisodeListItem from "./EpisodeListItem";
import {
  EpisodeAccessSet,
  getWaitAndFreeWaiting,
  seriesAccessToepisodeAccessSet,
} from "@lib/utils";
import { EpisodeDisplayedOn } from "../../../types/types";
import { useToast } from "app/hooks";
import { logError } from "@lib/logger";
import { sessionStore } from "@lib/sessionStore";

const PER_PAGE = 30;
export function SeriesEpisodeListDynamicLoader({
  seriesHash,
  episodeCount,
  currentEpisodeId,
  displayedOn,
}: {
  seriesHash: string;
  episodeCount?: number;
  currentEpisodeId?: string;
  displayedOn: EpisodeDisplayedOn;
}) {
  const [sortOrPage, setSortOrPage] = useState<"new" | number>();
  const [showAllPages, setShowAllPages] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [mySeries, setMySeries] = useState<SeriesModel>();
  const [mySeriesAccess, setMySeriesAccess] = useState<SeriesAccessModel>();
  //session
  const { sessionStatus } = useSyncExternalStore(
    sessionStore.subscribe,
    sessionStore.getSnapshot,
    sessionStore.getServerSnapshot,
  );
  const { pushToast } = useToast();

  if (!episodeCount) {
    episodeCount = 1;
  }
  const sortItems = [];
  for (let i = 0; i < episodeCount / PER_PAGE; i++) {
    const idx = i;
    sortItems.push(
      <a
        className={"series-sort-link" + (sortOrPage === idx ? " mode-current" : "")}
        onClick={() => {
          setSortOrPage(idx);
        }}
        key={idx}
      >
        {i * PER_PAGE + 1}-{Math.min(episodeCount, i * PER_PAGE + PER_PAGE)}
      </a>,
    );
  }
  if (sortItems.length > 3) {
    //３つめに全表示アイテム
    sortItems.splice(
      2,
      0,
      <a
        className="series-sort-link"
        onClick={() => {
          setShowAllPages(true);
        }}
        key="all"
      >
        ...
      </a>,
    );
  }

  let episodeAccessSet: EpisodeAccessSet;
  if (mySeriesAccess) {
    episodeAccessSet = seriesAccessToepisodeAccessSet(mySeriesAccess);
  }

  useEffect(() => {
    if (!episodeCount) {
      episodeCount = 1;
    }
    if (sortOrPage !== undefined) {
      let startIdx = 1;
      let loadCount = PER_PAGE;
      if (sortOrPage === "new") {
        startIdx = Math.max(episodeCount - PER_PAGE + 1, 1);
        loadCount = Math.min(PER_PAGE, episodeCount);
      } else {
        startIdx = sortOrPage * PER_PAGE + 1;
        loadCount = Math.min(PER_PAGE, episodeCount - startIdx + 1);
      }
      const sort = sortOrPage;
      setIsLoading(true);
      //episode listの取得
      fetch(
        `/api/episodes?seriesHash=${seriesHash}&episodeFrom=${startIdx}&episodeTo=${startIdx + loadCount - 1}`, //memo:episodeFromは1から
      )
        .then((res) => res.json())
        .then((res) => {
          const series = (res.series as SeriesModel) || undefined;
          if (sort === "new") {
            series.episodes.reverse();
          }
          setMySeries((res.series as SeriesModel) || undefined);
        })
        .catch(() => {
          logError("server error: update episode list");
          pushToast("エピソードリストの更新に失敗しました。しばらく経ってから再度お試しください", {
            type: "error",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
      //accessの取得
      fetch(
        `/api/series/access?seriesHash=${seriesHash}&episodeFrom=${startIdx}&episodeTo=${startIdx + loadCount - 1}`, //memo:episodeFromは1から
      )
        .then((res) => res.json())
        .then((res) => {
          const access = (res.seriesAccess as SeriesAccessModel) || undefined;
          setMySeriesAccess(access);
        });
    }
  }, [sortOrPage]);

  return (
    <>
      <div
        className={
          "series-sort" +
          (showAllPages ? " mode-show-all" : "") +
          (isLoading ? " mode-loading" : "")
        }
      >
        <a
          className={"series-sort-link" + (sortOrPage === "new" ? " mode-current" : "")}
          onClick={() => {
            setSortOrPage("new");
          }}
        >
          最新話から
        </a>
        {sortItems}
      </div>
      {mySeries && (
        <div className={"series-eplist mode-dynamic"}>
          {mySeries.episodes.map((episode, index) => {
            return (
              <EpisodeListItem
                episode={episode}
                key={episode.id}
                waitAndFreeWaiting={getWaitAndFreeWaiting(mySeries.summary.seriesWaitOn)}
                access={episodeAccessSet?.getEpisodeAccessByEpisodeId(episode.id)}
                currentEpisodeId={currentEpisodeId}
                displayedOn={displayedOn}
                displayIndex={index}
              />
            );
          })}
        </div>
      )}
    </>
  );
}
